import { Box } from '@mui/material';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setLoginDetails } from '../../store/features/auth/authSlice';
import { RootState } from '../../store/store';
import CustomAutocomplete from '../../shared/AutoComplete';
import { notifyUser } from '../fiscalUpgrade/Utils/notifyUser';
import { saveUserProfile } from '../fiscalUpgrade/services/Settings/ProfileService';

interface LanguageOption {
    code: string;
    name: string;
    country_code: string;
}

const languages: LanguageOption[] = [
    { code: 'en', name: 'English', country_code: 'en' },
    { code: 'es', name: 'Spanish', country_code: 'es' }
];

const langMap: Record<string, string> = {
    English: 'en',
    Spanish: 'es',
    en: 'en',
    es: 'es'
};

const Languages: React.FC = () => {
    const dispatch = useDispatch();
    const userDetails = useSelector((state: RootState) => state.auth);
    const [selectedValue, setSelectedValue] = useState<LanguageOption | null>(null);
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    useEffect(() => {
        if (!userDetails.language) return;

        const langCode = langMap[userDetails.language] || 'en';
        const langObj =
            languages.find((l) => l.code === langCode) || languages[0];

        setSelectedValue(langObj);

        i18next.changeLanguage(langCode);
    }, [userDetails.language]);

    const changeLanguage = async (value: LanguageOption | null) => {
        if (!value) return;

        setSelectedValue(value);
        try {
            const response = await saveUserProfile(userDetails.id, { language: value.code });
            const updatedUser = response.data.data[0];

            dispatch(setLoginDetails({
                ...updatedUser,
                language: value.code
            }));

            i18next.changeLanguage(value.code);

            notifyUser(`The language has been updated to ${value.name}.`, 'success', 'Language');
        } catch (error) {
            console.error('Error updating language:', error);
            notifyUser(`Failed to update language. Please try again.`, 'error', 'Language');
        }
    };

    return (
        <Box sx={{ width: '20%', marginTop: 2 }}>
            <CustomAutocomplete
                size="small"
                options={languages}
                value={selectedValue}
                onChange={(_, newValue) => changeLanguage(newValue)}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.code === value?.code}
                label={translate('selectLanguage')}
                placeholder={translate('selectLanguage')}
            />
        </Box>
    );
};

export default Languages;
